import React, { useCallback, useEffect, useRef } from 'react'
import { useBottomSheetDrag, type SheetState } from './hooks/useBottomSheetDrag'
import { useKeyboardAdjust } from './hooks/useKeyboardAdjust'
import { useScrollLock } from './hooks/useScrollLock'

export interface AppDrawerProps {
  children: React.ReactNode
  isOpen: boolean
  onClose: () => void
  mode: 'bottom-sheet' | 'side-panel'
  draggable?: boolean
  width?: number | string
  side?: 'left' | 'right'
  adjustForKeyboard?: boolean
  /** Lock background page scroll via touchmove prevention. Use for
   *  full-width mobile bottom sheets. Do not use for partial-page widgets
   *  where the user should still scroll the page behind. */
  lockBodyScroll?: boolean
  /** Called when the bottom-sheet drag state changes ('half' or 'full'). */
  onSheetStateChange?: (state: SheetState) => void
  /** Additional class name(s) for the drawer root element. */
  className?: string
}

/**
 * Generic structural drawer shell. Content-agnostic.
 *
 * - Uses `position: fixed` (like Vaul/shadcn drawer) so the drawer overlays
 *   the page without affecting layout. Page content stays in normal flow.
 * - CSS custom properties `--app-drawer-top` and `--app-drawer-top-full`
 *   control the top edge position (inherited from parent container).
 * - Bottom-sheet mode: slides up from bottom via transform: translateY.
 * - Side-panel mode: slides from left/right via transform: translateX.
 * - When `draggable`, renders a drag handle with touch gestures.
 * - When `adjustForKeyboard`, handles iOS Safari virtual keyboard by
 *   adjusting bottom offset and using focus({ preventScroll: true }).
 * - Body scroll lock when open in bottom-sheet mode.
 * - Escape key triggers onClose.
 * - `hasBeenOpenedRef` pattern: skips rendering until first open to avoid
 *   CSS transition flash on mount.
 *
 * NO visual styling — no backgrounds, colors, borders. Only structural:
 * position, overflow, transitions, transforms.
 */
export const AppDrawer: React.FC<AppDrawerProps> = ({
  children,
  isOpen,
  onClose,
  mode,
  draggable = false,
  width,
  side = 'right',
  adjustForKeyboard = false,
  lockBodyScroll = false,
  onSheetStateChange,
  className: extraClassName,
}) => {
  // Skip rendering until the drawer has been opened at least once.
  // This prevents the close-state CSS transition from flashing on mount.
  const hasBeenOpenedRef = useRef(false)
  if (isOpen) {
    hasBeenOpenedRef.current = true
  }

  // Shared ref so useBottomSheetDrag knows when the keyboard is open
  // and can skip animating `top` (which causes iOS to scroll).
  const isKeyboardOpenRef = useRef(false)

  // Bottom sheet drag gestures
  const {
    sheetRef,
    sheetState,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    resetSheetState,
  } = useBottomSheetDrag({ onClose, onSheetStateChange, isKeyboardOpenRef })

  // iOS keyboard adjustment: shrink drawer above the virtual keyboard
  useKeyboardAdjust({
    enabled: adjustForKeyboard && isOpen,
    drawerRef: sheetRef,
    isKeyboardOpenRef,
  })

  // Touch-based scroll lock for mobile bottom sheets
  useScrollLock({
    enabled: lockBodyScroll && isOpen,
    drawerRef: sheetRef,
  })

  // Reset sheet state and dismiss keyboard when drawer closes
  const prevOpenRef = useRef(isOpen)
  useEffect(() => {
    if (prevOpenRef.current && !isOpen) {
      resetSheetState()
      // Blur any focused input inside the drawer to dismiss the keyboard
      const active = document.activeElement as HTMLElement | null
      if (active && sheetRef.current?.contains(active)) {
        active.blur()
      }
    }
    prevOpenRef.current = isOpen
  }, [isOpen, resetSheetState])

  // Body scroll lock when open in bottom-sheet mode.
  // IMPORTANT: Do NOT use position:fixed on document.body — on iOS Safari,
  // it conflicts with the virtual keyboard.
  useEffect(() => {
    if (mode === 'bottom-sheet' && isOpen) {
      document.documentElement.style.overflow = 'hidden'
      document.body.style.overflow = 'hidden'
    } else {
      document.documentElement.style.overflow = ''
      document.body.style.overflow = ''
    }
    return () => {
      document.documentElement.style.overflow = ''
      document.body.style.overflow = ''
    }
  }, [mode, isOpen])

  // Escape key handler
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault()
      onClose()
    }
  }, [onClose])

  if (!hasBeenOpenedRef.current) {
    return null
  }

  // Build class names
  const classNames = ['app-drawer']
  classNames.push(`app-drawer--${mode}`)
  if (mode === 'side-panel') {
    classNames.push(`app-drawer--${side}`)
  }
  if (!isOpen) {
    classNames.push('app-drawer--closed')
  }
  if (mode === 'bottom-sheet' && sheetState === 'full') {
    classNames.push('app-drawer--full')
  }
  if (extraClassName) {
    classNames.push(extraClassName)
  }

  const style: React.CSSProperties = {}
  if (mode === 'side-panel' && width) {
    style.width = typeof width === 'number' ? `${width}px` : width
  }

  return (
    <div
      className={classNames.join(' ')}
      ref={sheetRef}
      style={style}
      onKeyDown={handleKeyDown}
    >
      {mode === 'bottom-sheet' && draggable && (
        <div
          className="app-drawer__drag-handle"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        />
      )}
      {children}
    </div>
  )
}
